import { useBrand } from "@branding/useBrand";
import { AppCta } from "../MarketingLayout";

/**
 * Pricing: three tiers (Starter / Pro / Enterprise), all leading to a free
 * TRIAL — there is no permanent free tier. Prices are SAMPLE figures for display
 * only; no payment is collected here and no billing integration exists. Starter
 * and Pro CTAs route into the product's real sign-up; Enterprise is "Contact
 * sales" (a mailto derived from the brand, kept honest and white-label).
 */
export function Pricing() {
  const brand = useBrand();
  const salesEmail = `sales@${brand.name.trim().toLowerCase().replace(/[^a-z0-9]/g, "")}.com`;

  const tiers = [
    {
      name: "Starter",
      badge: null as string | null,
      price: "49",
      per: "/ entity / month",
      desc: "For a single UAE entity keeping its own books.",
      features: [
        "1 legal entity",
        "Live tie-out trial balance, P&L & balance sheet",
        "5% VAT tracking",
        "AED base currency",
        "CSV import / export",
        "Up to 2 users",
      ],
      cta: <AppCta className="mk-btn primary">Start free trial</AppCta>,
      featured: false,
    },
    {
      name: "Pro",
      badge: "Most popular",
      price: "149",
      per: "/ month",
      desc: "For multi-entity groups that consolidate and trade in FX.",
      features: [
        "Up to 5 entities + consolidated group view",
        "Everything in Starter",
        "Corporate-tax (9%) categorization",
        "Multi-currency with FX gain / loss",
        "Bank reconciliation",
        "Roles & access (owner / admin / member / viewer)",
        "Up to 15 users",
      ],
      cta: <AppCta className="mk-btn primary">Start free trial</AppCta>,
      featured: true,
    },
    {
      name: "Enterprise",
      badge: "Custom",
      price: null as string | null,
      per: "",
      desc: "For larger groups with many entities and bespoke needs.",
      features: [
        "Unlimited entities & users",
        "Everything in Pro",
        "Priority support & onboarding",
        "Custom retention & data controls",
        "Volume pricing",
      ],
      cta: (
        <a className="mk-btn secondary" href={`mailto:${salesEmail}`}>
          Contact sales
        </a>
      ),
      featured: false,
    },
  ];

  const matrix: { label: string; starter: string; pro: string; ent: string }[] = [
    { label: "Legal entities", starter: "1", pro: "Up to 5", ent: "Unlimited" },
    { label: "Consolidated group view", starter: "—", pro: "✓", ent: "✓" },
    { label: "Live tie-out statements", starter: "✓", pro: "✓", ent: "✓" },
    { label: "5% VAT tracking", starter: "✓", pro: "✓", ent: "✓" },
    { label: "Corporate-tax (9%) categorization", starter: "—", pro: "✓", ent: "✓" },
    { label: "Multi-currency + FX gain/loss", starter: "—", pro: "✓", ent: "✓" },
    { label: "Bank reconciliation", starter: "—", pro: "✓", ent: "✓" },
    { label: "CSV import / export", starter: "✓", pro: "✓", ent: "✓" },
    { label: "Roles & access control", starter: "Basic", pro: "✓", ent: "✓" },
    { label: "Users included", starter: "2", pro: "15", ent: "Unlimited" },
    { label: "Support", starter: "Email", pro: "Priority email", ent: "Priority + onboarding" },
  ];

  const cell = (v: string) =>
    v === "✓" ? (
      <span className="mk-yes" aria-label="Included">✓</span>
    ) : v === "—" ? (
      <span className="mk-no" aria-label="Not included">—</span>
    ) : (
      v
    );

  return (
    <>
      <section className="mk-section">
        <div className="mk-eyebrow">Pricing</div>
        <h1 className="mk-h1">Simple plans. Start with a free trial.</h1>
        <p className="mk-lead">
          Every plan starts with a 14-day free trial — no card required, no
          permanent free tier. Prices below are sample figures shown in AED for
          illustration; you set up billing later inside {brand.name}.
        </p>

        <div className="mk-tiers">
          {tiers.map((t) => (
            <div className={`mk-tier${t.featured ? " featured" : ""}`} key={t.name}>
              {t.badge ? <span className="tier-badge">{t.badge}</span> : null}
              <div className="tier-name">{t.name}</div>
              <div className="tier-price">
                {t.price ? (
                  <>
                    <span className="amt">AED {t.price}</span>
                    <span className="per">{t.per}</span>
                  </>
                ) : (
                  <span className="amt">Let’s talk</span>
                )}
              </div>
              <div className="tier-desc">{t.desc}</div>
              <ul>
                {t.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              {t.cta}
            </div>
          ))}
        </div>
        <p className="mk-hero-note" style={{ textAlign: "center" }}>
          Sample pricing for illustration only — no payment is collected on this
          site.
        </p>
      </section>

      <section className="mk-section" style={{ paddingTop: 0 }}>
        <h2 className="mk-h2">Compare plans</h2>
        <div className="mk-matrix-wrap">
          <table className="mk-matrix">
            <caption className="visually-hidden">
              Feature comparison across Starter, Pro, and Enterprise plans
            </caption>
            <thead>
              <tr>
                <th scope="col">Feature</th>
                <th scope="col" className="center">Starter</th>
                <th scope="col" className="center">Pro</th>
                <th scope="col" className="center">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {matrix.map((row) => (
                <tr key={row.label}>
                  <th scope="row" style={{ fontWeight: 550 }}>{row.label}</th>
                  <td className="center">{cell(row.starter)}</td>
                  <td className="center">{cell(row.pro)}</td>
                  <td className="center">{cell(row.ent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mk-cta">
        <div className="mk-section narrow">
          <h2 className="mk-h2">Try {brand.name} free for 14 days.</h2>
          <div className="mk-hero-cta">
            <AppCta className="mk-btn primary lg">Start your free trial</AppCta>
          </div>
        </div>
      </section>
    </>
  );
}
